import React, { useState, useEffect } from 'react';
import { AlertTriangle, Settings } from 'lucide-react';
import { useSupabaseData } from '../../hooks/useSupabaseData';
import { useNotificationSettings } from '../../hooks/useNotificationSettings';
import { useAdminState, useAdminDispatch } from '../../context/AdminContext';
import { useVacation } from '../../hooks/useVacation';

// Booking Flow Components
import ServiceSelection from './ServiceSelection';
import { LocationSelection } from './LocationSelection';
import { DateSelection } from './DateSelection';
import { TimeSelection } from './TimeSelection';
import { CustomerForm } from './CustomerForm';

// UI Components
import { BloomTopBar, ProgressStep } from '../UI/Bloom/flow';
import PaymentModal from '../Modals/PaymentModal';
import SuccessMessage from '../UI/SuccessMessage';
import Datenschutz from '../UI/Datenschutz';
import { T } from '../../utils/adminTheme';

// Lazy-loaded Admin Components
import AdminPanel from '../Admin/AdminPanel';

// Import utilities
import { isHoliday, getRegionFromPostalCode } from '../../utils/holidayUtils';
import { isOnVacation } from '../../utils/vacationUtils';
import { scheduleBookingNotifications } from '../../utils/notificationUtils';
import { generateTimeSlots, toLocalDateString } from '../../utils/timeUtils';
import { PaymentCompletionResult } from '../../types';

// Import types
import { 
  Location, Service, CustomerInfo
} from '../../types';

import LoginModal from '../Auth/LoginModal';
import { useAuth } from '../../context/AuthContext';
import ErrorBoundary from '../UI/ErrorBoundary';

const BeautyBookingSystem = () => {
  // Supabase-Hook
  const {
    locations: rawLocations,
    services: rawServices,
    bookings: supabaseBookings,
    loading: supabaseLoading,
    error: supabaseError,
    createBooking,
    convertLocation,
    convertService
  } = useSupabaseData();

  const { settings } = useNotificationSettings();

  // Konvertierte Daten
  const supabaseLocations = rawLocations.map(convertLocation);
  const supabaseServices = rawServices.map(convertService);

  // State — view aus sessionStorage wiederherstellen (verhindert Flash auf Refresh)
  const [currentView, setCurrentView] = useState(() =>
    sessionStorage.getItem('adminView') === 'admin' ? 'admin' : 'booking'
  );

  const setView = (v: string) => {
    setCurrentView(v);
    if (v === 'admin') sessionStorage.setItem('adminView', 'admin');
    else sessionStorage.removeItem('adminView');
  };
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [lastBookingId, setLastBookingId] = useState<number | null>(null);
  const [showDatenschutz, setShowDatenschutz] = useState(false);

  // Admin Context
  const {
    isAdmin,
    showAdminLogin,
    vacationPeriods,
  } = useAdminState();
  const adminDispatch = useAdminDispatch();
  
  // Vacation-Periods laden (auch für Kunden-Flow)
  useVacation();
  
  // Booking Flow State
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });

  // Kundenprofil: Email-Lookup für Zeitanpassung
  const [customerProfile, setCustomerProfile] = useState<{ service_durations?: Record<string, number>; name?: string } | null>(null);

  useEffect(() => {
    const email = customerInfo.email;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setCustomerProfile(null); return; }
    let cancelled = false;
    import('../../lib/supabase').then(({ supabase }) => {
      supabase.from('customers').select('name,service_durations').eq('email', email).maybeSingle()
        .then(({ data }) => { if (!cancelled) setCustomerProfile(data); });
    });
    return () => { cancelled = true; };
  }, [customerInfo.email]);

  // Effektive Behandlungsdauer: Kundenprofil überschreibt Service-Standard
  const effectiveDuration = selectedService
    ? (customerProfile?.service_durations?.[String(selectedService.id)] ?? selectedService.duration)
    : null;

  // Payment State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  // Event Handlers
  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);

    // 1) Ermittele Standorte, die für den Service erlaubt sind
    const serviceLocationIds = service.locationIds || service.location_ids || [];
    let availableLocations: typeof supabaseLocations;

    if (serviceLocationIds.length === 0) {
      // Keine Einschränkung → alle Standorte zulässig
      availableLocations = supabaseLocations;
    } else {
      availableLocations = supabaseLocations.filter(loc => serviceLocationIds.includes(loc.id));
    }

    // 2) Automatische Auswahl, wenn insgesamt nur ein Standort verfügbar ist
    if (availableLocations.length === 1) {
      setSelectedLocation(availableLocations[0]);
      setCurrentStep(3);
      scrollTop();
    } else if (supabaseLocations.length === 1) {
      setSelectedLocation(supabaseLocations[0]);
      setCurrentStep(3);
      scrollTop();
    } else {
      setCurrentStep(2);
      scrollTop();
    }
  };

  const handleLocationSelect = (location: Location) => {
    setSelectedLocation(location);
    setCurrentStep(3);
    scrollTop();
  };

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setCurrentStep(4);
    scrollTop();
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    setCurrentStep(5);
    scrollTop();
  };

  const handleCustomerInfoChange = (info: Partial<CustomerInfo>) => {
    setCustomerInfo((prev: CustomerInfo) => ({ ...prev, ...info }));
  };

  const handleCompleteBooking = async () => {
    if (selectedService?.prepayment) {
      setShowPaymentModal(true);
    } else {
      try {
        const booking = await createBooking({
          serviceId: Number(selectedService!.id),
          locationId: selectedLocation!.id,
          bookingDate: toLocalDateString(selectedDate!),
          bookingTime: selectedTime!,
          customerName: customerInfo.name,
          customerEmail: customerInfo.email,
          customerPhone: customerInfo.phone,
          customerAddress: customerInfo.address,
        });
        
        // Benachrichtigungen planen (optional)
        try {
          await scheduleBookingNotifications(booking as any);
        } catch (notifyError) {
          console.warn('Benachrichtigung konnte nicht geplant werden:', notifyError);
        }
        setLastBookingId((booking as any)?.id ?? null);
        setShowSuccessMessage(true);
      } catch (error) {
        console.error('Fehler beim Buchen:', error);
        alert('Fehler beim Buchen. Bitte versuchen Sie es erneut.');
      }
    }
  };

  const handlePaymentComplete = async (payment: PaymentCompletionResult) => {
    setShowPaymentModal(false);

    try {
      const booking = await createBooking({
        serviceId: Number(selectedService!.id),
        locationId: selectedLocation!.id,
        bookingDate: toLocalDateString(selectedDate!),
        bookingTime: selectedTime!,
        customerName: customerInfo.name,
        customerEmail: customerInfo.email,
        customerPhone: customerInfo.phone,
        customerAddress: customerInfo.address,
        needsPrepayment: payment.isPrepayment,
        prepaymentAmount: payment.isPrepayment ? payment.amount : 0,
        paymentMethod: payment.method,
      });

      // Benachrichtigungen planen
      try {
        await scheduleBookingNotifications(booking as any);
      } catch (notifyError) {
        console.warn('Benachrichtigung konnte nicht geplant werden:', notifyError);
      }

      setLastBookingId((booking as any)?.id ?? null);
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Fehler beim Buchen nach Zahlung:', error);
      alert('Fehler beim Erstellen der Buchung. Bitte kontaktieren Sie uns.');
    }
  };

  // adminPassword & handleAdminLogin entfernt

  const bookingSteps: ProgressStep[] = [
    { key: 'service', num: '01' },
    { key: 'location', num: '02' },
    { key: 'date', num: '03' },
    { key: 'time', num: '04' },
    { key: 'contact', num: '05' },
  ];

  const stepPrompts: Record<number, string> = {
    1: 'Welche Behandlung passt heute zu Ihnen?',
    2: 'Wo möchten Sie Ihren Termin buchen?',
    3: 'Welcher Tag fühlt sich richtig an?',
    4: 'Welche Uhrzeit passt am besten?',
    5: 'Nur noch Ihre Kontaktdaten.',
  };

  const handleFlowBack = () => {
    if (currentStep === 3 && selectedService?.locationIds?.length === 1) {
      setCurrentStep(1);
      return;
    }
    setCurrentStep(Math.max(1, currentStep - 1));
  };

  const { user, logout, isAuthLoading } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout fehlgeschlagen', err);
    } finally {
      adminDispatch({ type: 'SET_ADMIN', payload: false });
      setView('booking');
    }
  };

  // Nach erfolgreichem Login: nur wenn über Admin-Zahnrad eingeloggt
  useEffect(() => {
    const isAdminIntent = sessionStorage.getItem('adminLoginIntent') === 'true'
      || sessionStorage.getItem('adminView') === 'admin';
    if (user && !isAdmin && isAdminIntent) {
      adminDispatch({ type: 'SET_ADMIN', payload: true });
      adminDispatch({ type: 'SET_SHOW_ADMIN_LOGIN', payload: false });
      setView('admin');
    }
  }, [user]);

  // Session abgelaufen → zurück zur Buchung
  useEffect(() => {
    if (!isAuthLoading && !user && currentView === 'admin') {
      setView('booking');
    }
  }, [isAuthLoading, user]);

  // Loading Screen
  // Auth oder Daten laden → Spinner zeigen (verhindert grauen Bildschirm)
  if (isAuthLoading || supabaseLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ position: 'relative', margin: '0 auto 24px', width: 80, height: 80 }}>
            <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '4px solid #fce7f3' }} />
            <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: `4px solid ${T.accent}`, borderTopColor: 'transparent', animation: 'spin 0.9s linear infinite' }} />
            <div style={{ position: 'absolute', inset: 12, borderRadius: '50%', background: '#fdf2f8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>💄</div>
          </div>
          <h2 style={{ fontFamily: T.display, fontStyle: 'italic', fontSize: 22, color: T.text, margin: '0 0 4px' }}>Traum Beauty Cakar</h2>
          <p style={{ fontFamily: T.body, fontSize: 13, color: T.muted, margin: 0 }}>Einen Moment bitte…</p>
        </div>
      </div>
    );
  }

  // Error Screen
  if (supabaseError) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 16px' }}>
        <div style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: T.r.lg, maxWidth: 400, width: '100%', textAlign: 'center', padding: '32px 24px' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(239,68,68,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <AlertTriangle size={32} color="#ef4444" />
          </div>
          <h2 style={{ fontFamily: T.body, fontWeight: 700, fontSize: 18, color: T.text, margin: '0 0 8px' }}>Verbindungsfehler</h2>
          <p style={{ fontFamily: T.body, fontSize: 13, color: T.muted, margin: '0 0 24px' }}>Daten konnten nicht geladen werden.</p>
          <button onClick={() => window.location.reload()} style={{ width: '100%', padding: '12px', background: T.accent, border: 'none', borderRadius: T.r.md, fontFamily: T.body, fontSize: 14, fontWeight: 600, color: '#fff', cursor: 'pointer' }}>
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  if (showDatenschutz) {
    return <Datenschutz onClose={() => setShowDatenschutz(false)} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: T.bg, color: T.text }}>
      {currentView === 'booking' && (
        <div style={{ position: 'relative' }}>
          <BloomTopBar
            steps={bookingSteps}
            stepIdx={currentStep - 1}
            prompt={stepPrompts[currentStep] || stepPrompts[1]}
            onBack={handleFlowBack}
            brand={settings.system.companyName || 'Traum Beauty Cakar'}
          />
          <button
            onClick={() => {
              sessionStorage.setItem('adminLoginIntent', 'true');
              adminDispatch({ type: 'SET_SHOW_ADMIN_LOGIN', payload: true });
            }}
            title="Admin-Login"
            style={{
              position: 'absolute',
              right: 22,
              top: 60,
              width: 38,
              height: 38,
              borderRadius: 999,
              border: `1px solid ${T.line}`,
              background: T.surface,
              color: T.muted,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 6,
            }}
          >
            <Settings size={16} />
          </button>
        </div>
      )}

      {/* Admin Panel — vollbildschirm, außerhalb des maxWidth-Containers */}
      {currentView === 'admin' && isAdmin && (
        <ErrorBoundary>
          <AdminPanel onBack={() => setView('booking')} />
        </ErrorBoundary>
      )}

      {/* Booking-Inhalt + Admin-Login-Prompt (maxWidth 980) */}
      {(currentView === 'booking' || (currentView === 'admin' && !isAdmin)) && (
        <div style={{ maxWidth: 980, margin: '0 auto', padding: '22px 18px 42px' }}>
          {currentView === 'booking' && (
            <div>
              {currentStep === 1 && (
                <ServiceSelection
                  services={supabaseServices}
                  selectedService={selectedService}
                  onServiceSelect={handleServiceSelect}
                  popularIds={(() => {
                    const counts: Record<number, number> = {};
                    supabaseBookings.forEach(b => { counts[b.serviceId] = (counts[b.serviceId] || 0) + 1; });
                    const max = Math.max(...Object.values(counts), 0);
                    return max > 0 ? Object.entries(counts).filter(([, n]) => n >= max * 0.6).map(([id]) => Number(id)) : [];
                  })()}
                />
              )}
              {currentStep === 2 && (
                <LocationSelection
                  locations={supabaseLocations}
                  selectedLocation={selectedLocation}
                  onLocationSelect={handleLocationSelect}
                />
              )}
              {currentStep === 3 && selectedLocation && selectedService && (
                <DateSelection
                  selectedLocation={selectedLocation}
                  selectedDate={selectedDate}
                  onDateSelect={handleDateSelect}
                  isDateAvailable={(date: Date) => {
                    const region = getRegionFromPostalCode(selectedLocation.postalCode || '');
                    if (isHoliday(date, region)) return false;
                    if (isOnVacation(date, selectedLocation.id, vacationPeriods)) return false;
                    const weekday = date.getDay() === 0 ? 7 : date.getDay();
                    if (selectedLocation.openDays && !selectedLocation.openDays.includes(weekday)) {
                      const indCfg = (selectedLocation as any).individualOpenTimes?.[weekday];
                      if (!indCfg || indCfg.closed) return false;
                    }
                    const ind = (selectedLocation as any).individualOpenTimes?.[weekday];
                    if (ind && ind.closed) return false;
                    const slots = generateTimeSlots(
                      date, selectedService!, selectedLocation, supabaseBookings, supabaseServices,
                    );
                    return slots.length > 0;
                  }}
                />
              )}
              {currentStep === 4 && selectedLocation && selectedService && selectedDate && (
                <TimeSelection
                  selectedLocation={selectedLocation}
                  selectedService={selectedService}
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                  onTimeSelect={handleTimeSelect}
                  generateTimeSlots={() => generateTimeSlots(
                    selectedDate, selectedService!, selectedLocation, supabaseBookings, supabaseServices,
                  )}
                />
              )}
              {currentStep === 5 && selectedLocation && selectedService && selectedDate && selectedTime && (
                <CustomerForm
                  customerInfo={customerInfo}
                  onCustomerInfoChange={handleCustomerInfoChange}
                  selectedService={selectedService}
                  selectedLocation={selectedLocation}
                  selectedDate={selectedDate}
                  selectedTime={selectedTime}
                  onBack={() => setCurrentStep(4)}
                  onComplete={handleCompleteBooking}
                  onShowDatenschutz={() => setShowDatenschutz(true)}
                  effectiveDuration={effectiveDuration ?? undefined}
                  customerProfile={customerProfile ?? undefined}
                />
              )}
            </div>
          )}

          {currentView === 'admin' && !isAdmin && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <h2 style={{ fontFamily: T.body, fontWeight: 700, fontSize: 20, color: T.text, marginBottom: 8 }}>Admin-Bereich</h2>
              <p style={{ color: T.muted, fontFamily: T.body, marginBottom: 24 }}>Bitte melden Sie sich an, um das Admin Panel zu verwenden.</p>
              <button
                onClick={() => adminDispatch({ type: 'SET_SHOW_ADMIN_LOGIN', payload: true })}
                style={{ background: T.accent, color: '#fff', border: 'none', borderRadius: T.r.sm, padding: '10px 20px', fontFamily: T.body, fontWeight: 600, cursor: 'pointer' }}
              >
                Anmelden
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      {showAdminLogin && (
        <LoginModal
          onClose={() => adminDispatch({ type: 'SET_SHOW_ADMIN_LOGIN', payload: false })}
          onSuccess={() => {
            adminDispatch({ type: 'SET_ADMIN', payload: true });
            adminDispatch({ type: 'SET_SHOW_ADMIN_LOGIN', payload: false });
            setView('admin');
          }}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedService && (
        <PaymentModal
          show={showPaymentModal}
          service={selectedService}
          onClose={() => setShowPaymentModal(false)}
          onComplete={handlePaymentComplete}
        />
      )}

      {/* Buchung bestätigt */}
      {showSuccessMessage && (
        <SuccessMessage
          service={selectedService}
          location={selectedLocation}
          date={selectedDate}
          time={selectedTime}
          bookingId={lastBookingId}
          customerEmail={customerInfo.email}
          onAddAnother={() => {
            setShowSuccessMessage(false);
            setLastBookingId(null);
            setCurrentStep(1);
            setSelectedService(null);
            setSelectedLocation(null);
            setSelectedDate(null);
            setSelectedTime(null);
            // customerInfo bleibt erhalten
          }}
          onClose={() => {
            setShowSuccessMessage(false);
            setLastBookingId(null);
            setCurrentStep(1);
            setSelectedService(null);
            setSelectedLocation(null);
            setSelectedDate(null);
            setSelectedTime(null);
            setCustomerInfo({ name: '', email: '', phone: '', address: '', notes: '' });
          }}
        />
      )}
    </div>
  );
};

export default BeautyBookingSystem;
